import type { RangeValue } from '@heroui/react';
import type { DateValue } from '@internationalized/date';
import type { EProductType } from '@quesmed/types/models';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { EDateFilter } from '@/config';
import type { OmitFunctions } from '@/types/';
import { qsStorage } from '@/utils';
import { createSelectors } from './selector';

interface CommentSlice {
  questionIds: number[];
  search: string;
  createdAt: DateValue | undefined;
  productId: EProductType | null;
  review: boolean | null;
  cursor: number | null;
  hasMore: boolean;
  dateFilter: EDateFilter | null;
  dateRange: RangeValue<DateValue> | undefined;
  range: { from: Date | null | number; to: Date | null | number };

  setDateFilter: (dateFilter: EDateFilter | null) => void;
  setQuestionIds: (questionIds: number[]) => void;
  setSearch: (search: string) => void;
  setCreatedAt: (createdAt: DateValue | undefined) => void;
  setProductId: (productId: EProductType | null) => void;
  setReview: (review: boolean | null) => void;
  setCursor: (cursor: number | null) => void;
  setHasMore: (hasMore: boolean) => void;
  resetFilters: () => void;
  hasFilters: () => boolean;
  setDateRange: (dateRange: RangeValue<DateValue>) => void;
  setRange: (range: { from: Date | null | number; to: Date | null | number }) => void;
}

const initialState: OmitFunctions<CommentSlice> = {
  questionIds: [],
  search: '',
  createdAt: undefined,
  productId: 0,
  review: null,
  cursor: null,
  hasMore: true,
  dateFilter: null,
  dateRange: undefined,
  range: { from: null, to: null },
};

const commentSlice = create<CommentSlice>()(
  persist(
    (set, get) => ({
      ...initialState,
      setQuestionIds: (questionIds) => {
        set({ questionIds, cursor: null, hasMore: true });
      },
      setSearch: (search) => {
        set({ search, cursor: null, hasMore: true });
      },
      setCreatedAt: (createdAt) => {
        set({ createdAt, cursor: null, hasMore: true });
      },
      setProductId: (productId) => {
        set({ productId, cursor: null, hasMore: true });
      },
      setReview: (review) => {
        set({ review, cursor: null, hasMore: true });
      },
      setCursor: (cursor) => {
        set({ cursor });
      },
      setHasMore: (hasMore) => {
        set({ hasMore });
      },
      setDateFilter: (dateFilter) => set({ dateFilter }),
      setDateRange: (dateRange) => set({ dateRange }),
      setRange: (range) => set({ range }),
      resetFilters: () => {
        set({
          questionIds: initialState.questionIds,
          search: initialState.search,
          createdAt: initialState.createdAt,
          productId: initialState.productId,
          review: initialState.review,
          cursor: initialState.cursor,
          hasMore: initialState.hasMore,
        });
      },
      hasFilters: () => {
        const { questionIds, search, createdAt, productId, review, dateFilter } = get();
        return (
          questionIds.length > 0 ||
          search !== initialState.search ||
          createdAt !== undefined ||
          productId !== initialState.productId ||
          review !== initialState.review ||
          dateFilter !== initialState.dateFilter
        );
      },
    }),
    {
      name: 'comment-filters',
      storage: createJSONStorage(() => qsStorage),
      partialize: (state) => ({
        questionIds: state.questionIds,
        search: state.search,
        createdAt: state.createdAt,
        productId: state.productId,
        review: state.review,
        dateFilter: state.dateFilter,
      }),
    },
  ),
);

export const useComment = createSelectors(commentSlice);
